import { useEffect, useRef, useCallback } from "react";
import abcjs from "abcjs";
import { cleanAbc } from "../lib/abcUtils";

// Responsive notation: picks measures-per-line from the container width so notes
// stay large on mobile/tablet, and re-renders on resize / orientation change.
// When respectBreaks is true, the user's own line breaks are honoured (no reflow).
export const AbcNotation = ({ abc, params = {}, className = "", fit = false, respectBreaks = false, zoom = 1 }) => {
  const ref = useRef(null);
  // params arrives as a fresh object literal on every parent render — key on its
  // CONTENT so typing in a search box or ABC editor doesn't re-run abcjs needlessly.
  const paramsKey = JSON.stringify(params);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const render = useCallback(() => {
    if (!ref.current) return;
    if (!abc) {
      ref.current.innerHTML = "";
      return;
    }
    const width = ref.current.clientWidth || 600;
    // fewer measures per line on narrow screens => bigger notes (tuned to ~75%)
    let perLine;
    if (fit) {
      // fullscreen/fit: never overflow the viewport width, even on phones
      if (width < 380) perLine = 2;
      else if (width < 560) perLine = 3;
      else if (width < 900) perLine = 4;
      else perLine = 6;
    } else if (width < 420) perLine = 3;
    else if (width < 700) perLine = 4;
    else perLine = 5;

    const p = paramsRef.current;
    const opts = {
      add_classes: true,
      staffwidth: Math.max(160, width - 6),
      paddingtop: 4,
      paddingbottom: 4,
      paddingright: 0,
      paddingleft: 0,
      ...p,
    };
    if (!respectBreaks && !p.wrap) {
      opts.wrap = { minSpacing: 1.6, maxSpacing: 2.6, preferredMeasuresPerLine: perLine };
    }
    if (zoom !== 1) {
      // bigger notes = fewer bars per line, the whole width still used
      opts.scale = zoom;
      opts.staffwidth = Math.max(160, width - 6) / zoom - 4;
      if (opts.wrap) {
        opts.wrap = { ...opts.wrap, preferredMeasuresPerLine: Math.max(1, Math.round(opts.wrap.preferredMeasuresPerLine / zoom)) };
      }
    }
    try {
      abcjs.renderAbc(ref.current, cleanAbc(abc), opts);
    } catch (e) {
      /* ignore malformed intermediate ABC while editing */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abc, paramsKey, fit, respectBreaks, zoom]);

  useEffect(() => {
    // small debounce: keeps typing in the ABC editor smooth
    const t = setTimeout(render, 90);
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(render);
    });
    if (ref.current) ro.observe(ref.current);
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [render]);

  return <div ref={ref} className={`abc-render ${className}`} />;
};
